import { useState, useRef } from "react";
import { Switch } from "@/components/ui/switch";
import { Loader2, Check, X } from "lucide-react";
import { cn } from "@/lib/utils";

interface InlineSwitchFieldProps {
  value: boolean;
  onSave: (value: boolean) => Promise<void>;
  className?: string;
  disabled?: boolean;
  label?: string;
  testId?: string;
}

type SaveState = "idle" | "saving" | "success" | "error";

export function InlineSwitchField({
  value,
  onSave,
  className,
  disabled = false,
  label,
  testId,
}: InlineSwitchFieldProps) {
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const saveTimeoutRef = useRef<NodeJS.Timeout>();

  const handleCheckedChange = async (checked: boolean) => {
    if (checked === value) {
      return;
    }

    setSaveState("saving");

    try {
      await onSave(checked);
      setSaveState("success");

      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      saveTimeoutRef.current = setTimeout(() => {
        setSaveState("idle");
      }, 2000);
    } catch {
      setSaveState("error");

      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      saveTimeoutRef.current = setTimeout(() => {
        setSaveState("idle");
      }, 3000);
    }
  };

  const SaveIndicator = () => {
    if (saveState === "saving") {
      return <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />;
    }
    if (saveState === "success") {
      return <Check className="h-4 w-4 text-green-600 dark:text-green-400" />;
    }
    if (saveState === "error") {
      return <X className="h-4 w-4 text-destructive" />;
    }
    return null;
  };

  return (
    <div className="space-y-1">
      {label && (
        <label className="text-sm font-medium text-muted-foreground">
          {label}
        </label>
      )}
      <div className="relative flex items-center gap-3">
        <Switch
          checked={value}
          onCheckedChange={handleCheckedChange}
          disabled={disabled || saveState === "saving"}
          className={cn(className)}
          data-testid={testId}
        />
        <span className="text-sm">
          {value ? "Yes" : "No"}
        </span>
        <div className="w-4 flex items-center justify-center">
          <SaveIndicator />
        </div>
      </div>
    </div>
  );
}
